import React, { useEffect } from "react";
import axios from "axios";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { Release } from "types/release";

export interface IFilters {
  filterArtistIds?: string[];
  filterPlatformIds?: string[];
  filterLabelIds?: string[];
  filterContributorIds?: string[];
}

export const useReleases = (
  status: string = '',
  currentPage: number = 0,
  pageCount: number = 5,
  filterArtistIds: IFilters['filterArtistIds'],
  filterPlatformIds: IFilters['filterPlatformIds'],
  filterLabelIds: IFilters['filterLabelIds'],
  filterContributorIds: IFilters['filterContributorIds'],
) => {
  const [releases, setReleases] = React.useState<Release[]>([]);
  const [fetching, setFetching] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [error, setError] = React.useState<Error | null>(null);
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);

  useEffect(() => {
    (async () => await getReleases(
      status,
      currentPage,
      pageCount,
      { filterArtistIds, filterPlatformIds, filterLabelIds, filterContributorIds }
    ))();
  }, [status, currentPage, pageCount, filterArtistIds, filterPlatformIds, filterLabelIds, filterContributorIds]);

  const getReleases = async (
    status: string = '',
    currentPage: number = 0,
    pageCount: number = 5,
    filters: IFilters = {},
  ) => {
    try {
      setFetching(true);
      setError(null);
      const queryParams = new URLSearchParams({
        status,
        // Backend sorting: prefix '-' for DESC. We use actual field name from API payload (`created_at`).
        sort: '-created_at',
        page: currentPage,
        per_page: pageCount,
        artist_ids: prepareFilter('artist_ids', filters.filterArtistIds),
        role_ids: prepareFilter('role_ids', filters.filterContributorIds),
        label_id: prepareFilter('label_id', filters.filterLabelIds),
        platform_ids: prepareFilter('platform_ids', filters.filterPlatformIds),
      } as any);
      const query = Array.from(queryParams.entries())
        .map(param => {
          if (['artist_ids', 'role_ids', 'label_id', 'platform_ids'].includes(param[0])) {
            return param[1];
          }
          if (param[1]) {
            return `${param[0]}=${param[1]}`;
          }
          return '';
        })
        .filter(param => !!param)
        .join('&');
      const response = await axios.get(
        `${API_BASE_URL}${API_URLS.Releases}?${query}`,
        { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } }
      );
      setReleases(response.data.data);
      setTotal(response.data.meta.total);
    } catch (e: any) {
      console.error("[useReleases] Failed to load releases", e);
      setReleases([]);
      setTotal(0);
      setError(e instanceof Error ? e : new Error("Failed to load releases"));
    } finally {
      setFetching(false);
    }
  };

  return { releases, total, fetching, error };
};

const prepareFilter = (key: string, ids?: string[]) => {
  return ids?.length ? ids.map(id => `${key}[]=${id}`).join("&") : '';
};
